import React, { useContext } from "react";
import {
  Box,
  Alert,
  AlertTitle,
  Button,
  Chip,
  Collapse,
  Typography,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import BlockIcon from "@mui/icons-material/Block";
import { AuthContext } from "context/Auth";
import { useRateLimit } from "hooks/useRateLimit";
import { useNavigate } from "react-router-dom";
import ChatComposer from "components/chat/ChatComposer";

const CustomMessageInput = ({
  inputValue,
  setInputValue,
  handleSendRequest,
  userData = null,
  disabled = false,
  hasMessages = true,
}) => {
  const { subscription, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const rateLimit = useRateLimit(userData, subscription);

  // Override rate limit state if disabled prop is provided
  const isDisabled = disabled || rateLimit.isRateLimited;

  // Clear input when user becomes rate limited so they can see the placeholder
  React.useEffect(() => {
    if (isDisabled && inputValue.trim()) {
      setInputValue("");
    }
  }, [isDisabled, inputValue, setInputValue]);

  const getWarningMessage = () => {
    if (rateLimit.effectivePercentage >= 95) {
      return rateLimit.messagePercentage > rateLimit.tokenPercentage
        ? "Almost out of daily messages"
        : "Almost out of monthly tokens";
    } else if (rateLimit.effectivePercentage >= 90) {
      return rateLimit.messagePercentage > rateLimit.tokenPercentage
        ? "Running low on daily messages"
        : "Running low on monthly tokens";
    }
    return "Running low on capacity";
  };

  const getErrorMessage = () => {
    const isMessageLimit = rateLimit.messagePercentage >= 100;
    const baseMessage = isMessageLimit
      ? "Out of daily messages"
      : "Out of monthly tokens";

    if (userData?.fromWordPress || subscription) {
      return `${baseMessage}. Resets ${
        isMessageLimit ? "tomorrow" : "next month"
      }.`;
    }
    return `${baseMessage}. Upgrade for ${
      isMessageLimit ? "unlimited messages" : "more tokens"
    }.`;
  };

  const handleUpgrade = () => {
    navigate("/dashboard/upgrade");
  };

  return (
    <Box sx={{ width: "100%", position: "relative" }}>
      {/* Alert Container - Position changes based on hasMessages */}
      <Box
        sx={{
          position: hasMessages ? "absolute" : "relative",
          bottom: hasMessages ? "100%" : "auto", // Position directly above the input only when hasMessages
          left: 0,
          right: 0,
          zIndex: 1000,
          mb: hasMessages ? 0 : 2, // Add margin bottom when not absolute
        }}
      >
        {/* Warning Alert - Shows at 80-99% usage */}
        <Collapse in={rateLimit.shouldShowWarning}>
          <Alert
            severity="warning"
            icon={<WarningAmberIcon />}
            sx={{
              mb: 0.5,
              mx: 0, // Remove horizontal margins to match input width
              borderRadius: 2,
              "& .MuiAlert-message": {
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
              },
            }}
            action={
              rateLimit.canUpgrade ? (
                <Button
                  size="small"
                  variant="outlined"
                  color="warning"
                  onClick={handleUpgrade}
                  sx={{ ml: 1, whiteSpace: "nowrap" }}
                >
                  Upgrade
                </Button>
              ) : null
            }
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="body2">{getWarningMessage()}</Typography>
              <Chip
                label={`${Math.round(rateLimit.effectivePercentage)}% used`}
                size="small"
                color="warning"
                variant="outlined"
              />
            </Box>
          </Alert>
        </Collapse>

        {/* Error Alert - Shows at 100% usage */}
        <Collapse in={rateLimit.isRateLimited}>
          <Alert
            severity="error"
            icon={<BlockIcon />}
            sx={{
              mb: 0.5,
              mx: 0,
              borderRadius: 2,
              "& .MuiAlert-message": {
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
              },
            }}
            action={
              rateLimit.canUpgrade ? (
                <Button
                  size="small"
                  variant="contained"
                  color="error"
                  onClick={handleUpgrade}
                  sx={{ ml: 1, whiteSpace: "nowrap" }}
                >
                  Upgrade
                </Button>
              ) : null
            }
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {getErrorMessage()}
              </Typography>
            </Box>
          </Alert>
        </Collapse>
      </Box>

      {/* Input Area - Hide when disabled and no messages */}
      {!(isDisabled && !hasMessages) && (
        <ChatComposer
          value={inputValue}
          onChange={setInputValue}
          onSubmit={handleSendRequest}
          disabled={isDisabled}
          autoFocus
          placeholder={
            isDisabled
              ? userData?.fromWordPress
                ? "Daily limit reached for this module. Resets tomorrow."
                : rateLimit.canUpgrade
                ? "Upgrade to continue messaging."
                : "Rate limit reached."
              : hasMessages
              ? "Write a message"
              : "Ask Medical SuperIntelligence"
          }
        />
      )}
    </Box>
  );
};

export default CustomMessageInput;
